// @Version: 1.0.2
import { Component, EventEmitter, Output, forwardRef, Input, OnDestroy, signal, OnChanges, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, FormGroup, FormBuilder, Validators, ControlValueAccessor } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import {  debounceTime, map} from 'rxjs/operators';
import { ErrorMessagesService } from '../../../services/errorMessages/error-messages.service';
import { ValidatorsService } from '../../../services/validators/validators.service';
import { IErrorCodes, IFieldItem, ITEMTYPE, VALIDATORS } from '../field-item.model';
import { FieldItemCombofieldService } from './combofield.service';
import { FC_COMBOFIELD } from './combofield.model';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-combofield',
   templateUrl: './combofield.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemCombofieldComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemCombofieldComponent),
         multi: true
      },
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemCombofieldComponent implements OnDestroy, OnChanges, Validators, ControlValueAccessor {

   public FC_COMBOFIELD = FC_COMBOFIELD; 
   public VALIDATORS = VALIDATORS;

   public done$$ = signal(false);

   public fgCombofield: FormGroup = this.fb.group({});

   public hideRequireMaker: boolean = false;

   private fieldItem_: IFieldItem = {} as IFieldItem;
   private errorCodes_: IErrorCodes = {} as IErrorCodes;
   private selection_: string[] = [];
   private sub_?: Subscription;
 
   @Input() clearValueIfDisabled: boolean = true;
   
   @Input() 
   get fieldItem (): IFieldItem {
     return this.fieldItem_;
   }
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value;
   }

   @Input()
   set value (val: string) {  
     if (typeof this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD) != 'undefined' && val) this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.setValue(val);
   }
   get value (): string {
      return this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.value ?? '';
   }

   @Output() isRequiredMarker = new EventEmitter<boolean>();

   get error (): IErrorCodes {
      return this.errorCodes_;
   }

   get selection (): string[] {
      return this.selection_;
   }

   constructor (private sFieldItemCombofield: FieldItemCombofieldService, private fb: FormBuilder, private sErrorMessages: ErrorMessagesService, private sValidators : ValidatorsService) {}   
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.errorCodes_ = this.sErrorMessages.setErrorCodes(changes.fieldItem.currentValue.attribute_2 ?? '', TRANSLATE_PATH.FIELD_ITEM_ERROR);
         this.selection_ = this.sFieldItemCombofield.setSelections(<string>changes.fieldItem.currentValue.attribute_3);
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }
   
   ngOnDestroy (): void {
      this.reset();
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgCombofield.updateValueAndValidity({emitEvent: true, onlySelf: true});
      return this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD) ? this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)!.valid || this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)!.disabled ? null : this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)!.errors : null;
   }

   writeValue (value: string | undefined | null): void {
      this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.setValue(value ? value : undefined);
      if (typeof value == 'string' && value.trim().length > 0) this.setDirtyTouched(); 
      this.onValidationChange();
   }
   private setDirtyTouched (): void {
     this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.markAsDirty(); 
     this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.markAsTouched();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }

   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }

   private onTouched () {}

   private onChange (_: string) {}

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)) isDisabled ? this.setDisabledControls() : this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.enable();
      this.onValidationChange();
   }

   onValidationChange: any = () => {}; 

   private reset (): void {
      this.done$$.set(false);
      this.fieldItem_ = {} as IFieldItem;
      this.errorCodes_ = {} as IErrorCodes;
      this.selection_ = [];
      this.fgCombofield = this.fb.group({});
   }


   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) this.sub_.unsubscribe();
      this.fgCombofield = this.fb.group({
         [FC_COMBOFIELD.COMBOFIELD]: this.fb.control({value: fieldItem.content, disabled: false } , {validators: this.sValidators.setValidators(this.fieldItem, ITEMTYPE.combofield), updateOn: 'change'})
      })

      this.sub_ = this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.valueChanges.pipe(
         debounceTime(500)
      ).subscribe(() => {
         if (!this.value && this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.pristine) return;
         this.onChange(this.value);
         this.onTouched();
      })

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required && !this.hideRequireMaker);

      return true;
   }

   private setDisabledControls (): void {
      this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.disable();
      if (this.clearValueIfDisabled) this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.setValue("");
      this.fgCombofield.get(FC_COMBOFIELD.COMBOFIELD)?.markAsTouched();
   }
}